import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp, MaskedReveal, SectionLabel } from "@/components/Motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "expecting", label: "Expecting" },
  { id: "new_mama", label: "New mama" },
  { id: "mama", label: "Mama" },
  { id: "prefer_not_to_say", label: "Neither, would just like to receive it ❤️" },
];

const EMPTY = {
  name: "", phone: "", email: "",
  address_line1: "", address_line2: "",
  city: "", state: "", pincode: "",
  motherhood_stage: "", marketing_consent: false,
};

function Field({ label, required, ...inputProps }) {
  return (
    <div>
      <label className="overline text-ink/60 block mb-3">
        {label}{required && <span className="text-terracotta"> *</span>}
      </label>
      <input
        required={required}
        className="w-full bg-transparent border-b border-ink/30 py-3 font-serif text-2xl text-ink outline-none focus:border-ink transition-colors placeholder:text-ink/30"
        {...inputProps}
      />
    </div>
  );
}

export default function FirstEnvelope() {
  const [status, setStatus] = useState(null); // {mode: 'open'|'waitlist'|'closed'}
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [doneType, setDoneType] = useState("confirmed");
  const [duplicate, setDuplicate] = useState(false);

  useEffect(() => {
    axios.get(`${API}/first-envelope/status`).then((r) => setStatus(r.data)).catch(() => setStatus({ mode: "open" }));
  }, []);

  // Lock background scroll while the success modal is open.
  useEffect(() => {
    if (done) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [done]);

  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.type === "checkbox" ? ev.target.checked : ev.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    if (!/^[1-9]\d{5}$/.test(form.pincode.trim())) {
      toast.error("Please enter a valid 6-digit Indian PIN code.");
      return;
    }
    setBusy(true);
    setDuplicate(false);
    try {
      const isWaitlist = status?.mode === "waitlist";
      const payload = { ...form, waitlist_consent: isWaitlist ? !!form.waitlist_consent : false };
      const res = await axios.post(`${API}/first-envelope/register`, payload);
      if (res.data.duplicate) {
        setDuplicate(true);
      } else {
        setDoneType(res.data.registration_type || (isWaitlist ? "waitlisted" : "confirmed"));
        setDone(true);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      if (e?.response?.status === 409) {
        setStatus({ mode: "closed" });
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}/first-envelope`;
  const waText = encodeURIComponent(
    `The Safe Mama Editorial is sending its first envelope to mums completely free 💌 I just registered for mine and thought you might like one too: ${shareUrl}`
  );

  return (
    <div className="pt-10 md:pt-16 pb-24 md:pb-32" data-testid="first-envelope-page">
      {/* ---- Hero copy ---- */}
      <section className="editorial-container max-w-3xl">
        <SectionLabel number="Intro" label="The First Envelope" />
        <h1 className="mt-8 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-ink" data-testid="first-envelope-heading">
          Your first Safe Mama envelope is <em className="italic text-terracotta">on us.</em> 💌
        </h1>

        <div className="mt-10 font-serif text-lg md:text-xl leading-[1.65] text-ink/85 space-y-6">
          <p>
            We've been creating something for mothers — slowly, thoughtfully, and with a lot of heart.
            And before we ask you to subscribe to it, we'd simply like you to experience it.
          </p>
          <p>So we're sending out our first Safe Mama Editorial envelope completely free.</p>
          <p>
            Inside, you'll find a real, anonymous story from another mother, nourishment and meal guidance,
            something from the Indian kitchen, our Expert Corner, and a few thoughtful little things we think
            mothers deserve to receive in the mail.
          </p>
          <p>
            For our introductory envelope, we've chosen one real, anonymous story for every mama — one that
            speaks to an experience that can stretch across different stages of motherhood.
          </p>
          <p>
            Our regular editions are a little more personal. What arrives in your envelope is curated around
            where you are in your journey — whether you're expecting or already navigating motherhood.
          </p>
          <p>
            But this first one?<br />
            This is simply our way of introducing ourselves.
          </p>
          <p className="font-serif italic text-2xl md:text-3xl text-ink leading-snug">
            No purchase. No subscription. Just a little mail from us to you.
          </p>
        </div>
      </section>

      {/* ---- Duplicate notice ---- */}
      {duplicate && (
        <section className="mt-20 editorial-container max-w-3xl" data-testid="first-envelope-duplicate">
          <FadeUp>
            <div className="border-t border-ink/15 pt-14">
              <h2 className="font-serif italic text-3xl md:text-4xl leading-tight text-ink">
                Looks like you're already on our list 💌
              </h2>
              <p className="mt-6 font-serif text-lg md:text-xl text-ink/75 leading-relaxed">
                Your first envelope has already been registered. It'll find its way to you.
              </p>
            </div>
          </FadeUp>
        </section>
      )}

      {/* ---- Closed state ---- */}
      {!duplicate && status?.mode === "closed" && (
        <section className="mt-20 editorial-container max-w-3xl" data-testid="first-envelope-closed">
          <FadeUp>
            <div className="border-t border-ink/15 pt-14">
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-ink">
                Registrations are paused for now. 💌
              </h2>
              <p className="mt-6 font-serif text-lg md:text-xl text-ink/75 leading-relaxed">
                We're not accepting new registrations at the moment. Do explore The Safe Mama Editorial in the meantime.
              </p>
              <Link to="/mail-club" className="btn-pill mt-10" data-testid="first-envelope-explore">
                Explore The Safe Mama Editorial <ArrowUpRight size={14} />
              </Link>
            </div>
          </FadeUp>
        </section>
      )}

      {/* ---- Waitlist state banner (form still shows below) ---- */}
      {!duplicate && status?.mode === "waitlist" && (
        <section className="mt-20 editorial-container max-w-3xl" data-testid="first-envelope-waitlist-banner">
          <FadeUp>
            <div className="border-t border-ink/15 pt-14">
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-ink">
                The first 200 envelopes have found their mamas.
              </h2>
              <p className="mt-6 font-serif text-lg md:text-xl text-ink/75 leading-relaxed">
                We've reached the first round of complimentary Safe Mama envelopes. If you'd still like one,
                join the waitlist below. If we're able to open up more spots, we'll get in touch.
              </p>
            </div>
          </FadeUp>
        </section>
      )}

      {/* ---- Registration form (also serves as the waitlist form) ---- */}
      {!duplicate && (status?.mode === "open" || status?.mode === "waitlist") && (
        <section className="mt-20 editorial-container max-w-3xl" data-testid="first-envelope-form-section">
          <FadeUp>
            <div className="border-t border-ink/15 pt-14">
              <div className="overline text-ink/60">
                {status?.mode === "waitlist" ? "Tell us where we'd send yours. 💌" : "Tell us where to send yours. 💌"}
              </div>

              <form onSubmit={submit} className="mt-10 space-y-10" data-testid="first-envelope-form">
                <Field label="Full name" required value={form.name} onChange={set("name")}
                  placeholder="Ananya Sharma" data-testid="fe-name" autoComplete="name" />
                <Field label="Mobile / WhatsApp number" required type="tel" value={form.phone} onChange={set("phone")}
                  placeholder="+91 98765 43210" data-testid="fe-phone" autoComplete="tel" />
                <Field label="Email address" required type="email" value={form.email} onChange={set("email")}
                  placeholder="you@post.com" data-testid="fe-email" autoComplete="email" />
                <Field label="Address line 1" required value={form.address_line1} onChange={set("address_line1")}
                  placeholder="Flat, house no., building" data-testid="fe-address1" autoComplete="address-line1" />
                <Field label="Address line 2" value={form.address_line2} onChange={set("address_line2")}
                  placeholder="Street, area, landmark (optional)" data-testid="fe-address2" autoComplete="address-line2" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                  <Field label="City" required value={form.city} onChange={set("city")}
                    placeholder="Mumbai" data-testid="fe-city" autoComplete="address-level2" />
                  <Field label="State" required value={form.state} onChange={set("state")}
                    placeholder="Maharashtra" data-testid="fe-state" autoComplete="address-level1" />
                </div>
                <Field label="PIN code" required inputMode="numeric" value={form.pincode} onChange={set("pincode")}
                  placeholder="400001" data-testid="fe-pincode" autoComplete="postal-code" maxLength={6} />

                <div>
                  <label className="overline text-ink/60 block mb-4">Where are you in your motherhood journey?</label>
                  <div className="flex flex-wrap gap-x-8 gap-y-4" data-testid="fe-stages">
                    {STAGES.map((s) => (
                      <label key={s.id} className="flex items-center gap-3 cursor-pointer" data-testid={`fe-stage-${s.id}`}>
                        <input
                          type="radio"
                          name="motherhood_stage"
                          required
                          value={s.id}
                          checked={form.motherhood_stage === s.id}
                          onChange={set("motherhood_stage")}
                          className="accent-terracotta h-4 w-4"
                        />
                        <span className="font-serif italic text-xl text-ink">{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer" data-testid="fe-consent-label">
                  <input
                    type="checkbox"
                    checked={form.marketing_consent}
                    onChange={set("marketing_consent")}
                    className="mt-1 accent-terracotta h-4 w-4"
                    data-testid="fe-consent"
                  />
                  <span className="font-serif text-base text-ink/80 leading-relaxed">
                    I'd also love to hear from The Safe Mama Editorial about future editions, stories,
                    launches and other things you think I might enjoy.
                  </span>
                </label>

                {status?.mode === "waitlist" && (
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="fe-waitlist-consent-label">
                    <input
                      type="checkbox"
                      required
                      checked={!!form.waitlist_consent}
                      onChange={set("waitlist_consent")}
                      className="mt-1 accent-terracotta h-4 w-4"
                      data-testid="fe-waitlist-consent"
                    />
                    <span className="font-serif text-base text-ink/80 leading-relaxed">
                      Please contact me if a complimentary Safe Mama envelope opens up for me. <span className="text-terracotta">*</span>
                    </span>
                  </label>
                )}

                <button type="submit" disabled={busy}
                  className="btn-pill mt-4 disabled:opacity-60" data-testid="fe-submit">
                  {busy
                    ? "Posting your name…"
                    : status?.mode === "waitlist"
                      ? <>Join the waitlist <ArrowUpRight size={14} /></>
                      : <>Send me my envelope <ArrowUpRight size={14} /></>}
                </button>

                <p className="text-sm font-sans text-ink/60 leading-relaxed" data-testid="fe-privacy-note">
                  We'll use your details to send your envelope and manage your registration. We won't sell or
                  share your personal information for third-party marketing. You can read more in our{" "}
                  <Link to="/privacy-policy" className="underline decoration-ink/40 hover:decoration-ink text-ink">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            </div>
          </FadeUp>
        </section>
      )}

      {/* ---- Success modal (only post-registration confirmation) ---- */}
      {done && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm"
          data-testid="first-envelope-success"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative bg-cream border border-ink/10 rounded-md max-w-lg w-full p-7 md:p-10 shadow-[0_30px_80px_-30px_rgba(46,38,32,0.5)] max-h-[92dvh] overflow-y-auto">
            <button
              onClick={() => setDone(false)}
              aria-label="Close"
              className="absolute top-4 right-4 text-ink/60 hover:text-ink text-2xl leading-none p-2"
              data-testid="first-envelope-modal-close"
            >
              ×
            </button>
            <div className="overline text-terracotta">{doneType === "waitlisted" ? "On the waitlist" : "Registered"}</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight text-ink">
              {doneType === "waitlisted"
                ? <>You're on the waitlist.</>
                : <>Your first envelope is officially on our list. 💌</>}
            </h2>
            <div className="mt-6 font-serif text-base md:text-lg leading-[1.6] text-ink/85 space-y-4">
              {doneType === "waitlisted" ? (
                <p>
                  Thank you for wanting The Safe Mama Editorial to find its way to you.
                  If we're able to open up more complimentary spots, we'll be in touch.
                </p>
              ) : (
                <>
                  <p>Thank you for letting The Safe Mama Editorial find its way into your home.</p>
                  <p>We hope opening it feels a little like hearing from a friend who gets it.</p>
                  <p>
                    Know another mama who might love one?<br />
                    Share the first envelope with her.
                  </p>
                </>
              )}
            </div>
            {doneType !== "waitlisted" && (
              <a
                href={`https://wa.me/?text=${waText}`}
                target="_blank"
                rel="noreferrer"
                className="btn-pill mt-8 w-full justify-center"
                data-testid="first-envelope-whatsapp"
              >
                <MessageCircle size={14} /> Share on WhatsApp
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
